import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import FormSection from "../../components/Common/Form/FormSection";
import MenuConfig from "../../../constants/MenuConfig";
import Module from "../../../constants/Module";
import { hasMenuAccess } from "../../../utils/ModuleAccessUtils";
import './ReportsHub.css';

/**
 * Relatórios agrupados por assunto.
 *
 * Cada item aponta para uma tela que já existe no seu módulo de origem: o hub é
 * só a porta de entrada, então module/menuKey precisam ser exatamente os mesmos
 * usados no Menu.js, senão o cartão aparece para quem não pode abrir a tela.
 */
const REPORT_GROUPS = [
    {
        key: "management",
        titleKey: "reports_hub.groups.management",
        items: [
            {
                module: Module.DASHBOARD,
                menuKey: "business_overview",
                route: "/dashboard",
                titleKey: "business_overview",
                descriptionKey: "reports_hub.items.business_overview",
                icon: "fa-solid fa-chart-pie",
                color: "#8b5cf6",
            },
            {
                module: Module.CASH_FLOW,
                menuKey: "dre_report",
                route: MenuConfig.cash_flow.dre_report.route,
                titleKey: MenuConfig.cash_flow.dre_report.title,
                descriptionKey: "reports_hub.items.dre_report",
                icon: "fa-solid fa-file-invoice-dollar",
                color: "#00BCD4",
            },
        ],
    },
    {
        key: "customers",
        titleKey: "reports_hub.groups.customers",
        items: [
            {
                module: Module.DASHBOARD,
                menuKey: "non_returning_customers",
                route: MenuConfig.dashboard.customer.nonReturning.route,
                titleKey: MenuConfig.dashboard.customer.nonReturning.title,
                descriptionKey: "reports_hub.items.non_returning_customers",
                icon: "fa-solid fa-user-clock",
                color: "#FF9800",
            },
            {
                module: Module.DASHBOARD,
                menuKey: "birthdays",
                route: MenuConfig.dashboard.birthdays.route,
                titleKey: MenuConfig.dashboard.birthdays.title,
                descriptionKey: "reports_hub.items.birthdays",
                icon: "fa-solid fa-cake-candles",
                color: "#E91E63",
            },
            {
                module: Module.APPOINTMENT,
                menuKey: "review",
                route: MenuConfig.appointment.review.route,
                titleKey: MenuConfig.appointment.review.title,
                descriptionKey: "reports_hub.items.reviews",
                icon: "fa-solid fa-star",
                color: "#FFC107",
            },
        ],
    },
    {
        key: "team",
        titleKey: "reports_hub.groups.team",
        items: [
            {
                module: Module.REPORTS,
                menuKey: "employee_statement",
                route: MenuConfig.reports.employee_statement.route,
                titleKey: MenuConfig.reports.employee_statement.title,
                descriptionKey: "reports_hub.items.employee_statement",
                icon: "fa-solid fa-receipt",
                color: "#0d9488",
            },
            {
                module: Module.REPORTS,
                menuKey: "financial_summary",
                route: MenuConfig.reports.financial_summary.route,
                titleKey: MenuConfig.reports.financial_summary.title,
                descriptionKey: "reports_hub.items.financial_summary",
                icon: "fa-solid fa-file-signature",
                color: "#0ea5e9",
            },
            {
                module: Module.REPORTS,
                menuKey: "pot",
                route: MenuConfig.reports.pot.route,
                titleKey: MenuConfig.reports.pot.title,
                descriptionKey: "reports_hub.items.pot",
                icon: "fa-solid fa-coins",
                color: "#f59e0b",
            },
            {
                module: Module.PERFORMANCE,
                menuKey: "dashboard",
                route: MenuConfig.performance.reports.route,
                titleKey: MenuConfig.performance.reports.title,
                descriptionKey: "reports_hub.items.performance",
                icon: "fa-solid fa-chart-simple",
                color: "#607D8B",
            },
        ],
    },
];

const ReportsHub = () => {

    const { t } = useTranslation();

    // Só entram os relatórios que o usuário já poderia abrir pelo menu do módulo.
    // O hub não pode virar atalho para o que a permissão esconde.
    const visibleGroups = REPORT_GROUPS
        .map(group => ({ ...group, items: group.items.filter(item => hasMenuAccess(item.module, item.menuKey)) }))
        .filter(group => group.items.length > 0);

    return (
        <>
            <PageTitle activeMenu={t('reports_center')} motherMenu={t('reports')} />

            {/* Avisos no padrão do sistema: o tema desenha o fundo e a borda do .alert nos dois modos. */}
            <Alert variant="info" className="py-2">
                <i className="fa-solid fa-circle-info me-2"></i>
                {t('reports_hub.subtitle')}
            </Alert>

            {visibleGroups.length === 0 && (
                <Alert variant="warning" className="py-2">
                    <i className="fa-solid fa-circle-info me-2"></i>
                    {t('reports_hub.empty')}
                </Alert>
            )}

            {/* Cada grupo é uma seção com superfície própria, o mesmo contêiner usado nos
                formulários do sistema: antes os cartões ficavam soltos direto sobre o fundo da
                página, sem o painel que separa um assunto do outro. */}
            {visibleGroups.map(group => (
                <FormSection
                    key={group.key}
                    className="reports-hub__group"
                    title={t(group.titleKey)}
                    compact
                >
                    <div className="reports-hub__grid">
                        {group.items.map(item => (
                            <Link key={`${item.module}:${item.menuKey}`} to={item.route} className="reports-hub__card">
                                <span className="reports-hub__icon" style={{ backgroundColor: `${item.color}1a`, color: item.color }}>
                                    <i className={item.icon}></i>
                                </span>
                                <span className="reports-hub__card-text">
                                    <strong>{t(item.titleKey)}</strong>
                                    <small>{t(item.descriptionKey)}</small>
                                </span>
                                <i className="fa-solid fa-chevron-right reports-hub__chevron"></i>
                            </Link>
                        ))}
                    </div>
                </FormSection>
            ))}
        </>
    );
};

export default ReportsHub;
